import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { catchError, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseModalComponent } from '@app/components/shared/base-modal/base-modal.component';
import { ModalControlsComponent } from '@app/components/shared/modal-controls/modal-controls.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { SubscriptionService } from '@app/pages/home-page/shop-settings/settings-subscription/services/subscription/subscription.service';
import { PlanModalPayload } from '@app/utils/interfaces/plan-modal-payload.interface';
import { StatusModalData } from '@app/utils/interfaces/status-modal.interface';

@Component({
  selector: 'app-change-subscription-modal',
  standalone: true,
  imports: [TranslatePipe, BaseModalComponent, ModalControlsComponent],
  templateUrl: './change-subscription-modal.component.html',
  styleUrl: './change-subscription-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeSubscriptionModalComponent {
  private readonly dialogRef = inject(DialogRef<ChangeSubscriptionModalComponent>);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly payload: PlanModalPayload = inject(MAT_DIALOG_DATA);
  protected readonly isDowngrade = signal(this.payload.activePlanId > this.payload.newPlanId);

  protected close(): void {
    this.dialogRef.close();
  }

  protected proceed(): void {
    const statusPayload: StatusModalData = {
      isSuccess: false,
      title: 'shop-settings.subscription.plans.status-modal.title',
      description: 'shop-settings.subscription.plans.status-modal.',
    };

    this.subscriptionService
      .updateSubscription(this.payload.newPlanId)
      .pipe(
        tap(() => {
          statusPayload.isSuccess = true;
          statusPayload.description += this.isDowngrade()
            ? 'downgrade-description'
            : 'upgrade-description';
          this.subscriptionService.activePlanId.set(this.payload.newPlanId);
        }),
        catchError(() => {
          statusPayload.description += 'payment-error';
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.close();
        this.dialog.open(StatusModalComponent, { data: statusPayload });
      });
  }
}
